import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const WEEK_DAYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function pad(n) { return String(n).padStart(2, "0"); }
function toYmd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function EmployeeAttendancePage() {
  const { selectedBranch, selectedSchool } = useBranch();
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [summary, setSummary] = useState({}); // {date: {marked_count, employees_count}}

  const yearMonth = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;

  const loadMonth = useCallback(async () => {
    if (!selectedBranch) { setSummary({}); return; }
    try {
      const { data } = await api.get("/attendance/month", {
        params: { branch_id: selectedBranch.id, year_month: yearMonth },
      });
      const map = {};
      (data || []).forEach((d) => { map[d.date] = d; });
      setSummary(map);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [selectedBranch, yearMonth]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  // Build 6-row grid starting on Sunday
  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay()); // back up to Sunday
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  const openDay = (d) => {
    if (!selectedBranch) {
      toast.error("Pick a branch first");
      return;
    }
    navigate(`/employees/attendance/${toYmd(d)}`);
  };

  return (
    <>
      <div className="mb-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">
          Employee Attendance
        </h1>
        <p className="text-brand-inkMuted mt-1">View and manage employee attendance by month.</p>
        {selectedSchool && (
          <p className="text-sm text-brand-inkMuted mt-0.5">
            School: <span className="font-semibold text-foreground">{selectedSchool.name}</span>
          </p>
        )}
      </div>

      {!selectedBranch ? (
        <NoBranchCard />
      ) : (
        <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 max-w-xl mx-auto" data-testid="attendance-calendar">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={goPrev}
              className="w-8 h-8 rounded-lg border border-brand-purple/15 flex items-center justify-center text-brand-inkMuted hover:bg-brand-purpleLight hover:text-foreground transition-colors"
              aria-label="Previous month"
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="font-heading text-base sm:text-lg font-semibold text-foreground tracking-tight" data-testid="current-month-label">
              {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
            </h2>
            <button
              onClick={goNext}
              className="w-8 h-8 rounded-lg border border-brand-purple/15 flex items-center justify-center text-brand-inkMuted hover:bg-brand-purpleLight hover:text-foreground transition-colors"
              aria-label="Next month"
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEK_DAYS.map((w) => (
              <div key={w} className="text-center text-[9px] sm:text-[10px] font-heading font-semibold text-brand-inkMuted/70 tracking-[0.1em] py-1">
                {w}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((d) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, today);
              const ymd = toYmd(d);
              const info = summary[ymd];
              const hasMarks = info && info.marked_count > 0;
              return (
                <button
                  key={ymd}
                  onClick={() => openDay(d)}
                  className={[
                    "h-10 sm:h-12 rounded-xl border text-left p-1.5 transition-all relative",
                    inMonth ? "border-brand-purple/10 hover:border-brand-purple/40 hover:bg-brand-purpleLight/40" : "border-brand-purple/5 opacity-50",
                    isToday ? "border-brand-purple/40" : "",
                    hasMarks ? "bg-[#E8F5E9] border-[#A5D6A7] hover:bg-[#C8E6C9]" : "",
                  ].join(" ")}
                  data-testid={`day-cell-${ymd}`}
                >
                  <span className={[
                    "inline-flex items-center justify-center text-xs font-heading font-semibold",
                    isToday ? "w-6 h-6 rounded-full bg-brand-purple text-white" : hasMarks ? "text-[#1B5E20]" : inMonth ? "text-foreground" : "text-brand-inkMuted",
                  ].join(" ")}>
                    {d.getDate()}
                  </span>
                  {hasMarks && !isToday && (
                    <span className="absolute bottom-1.5 right-1.5 w-1 h-1 rounded-full bg-[#2E7D32]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1">Attendance is scoped per branch. Use the dropdown in the header.</p>
    </div>
  );
}
